/**
 * Rhythmic Dictation generator — hear a rhythm in meter context, reproduce it
 * on a beat grid.
 *
 * Audio plan: 4-beat metronome click-in → pattern × 2 bars. No key cadence —
 * this exercise is pitch-neutral. Metro voice (MIDI 72) establishes the pulse;
 * target voice (MIDI 60) marks the attack positions.
 *
 * Items are fixed patterns (named rhythmic figures), so the same figure is
 * heard on every review. Only the tempo varies (seeded 80–120 BPM) to
 * prevent the user from memorizing a single "sound" duration.
 *
 * `expectedPattern` on meta is the binary grid string (e.g. "10101010") that
 * the grader compares against the user's input. `correctAnswerId` is left
 * empty, matching the melodic-dictation convention; Practice dispatches to
 * `gradeRhythmicGrid` instead of `gradeAnswer`.
 */

import { AudioPlanBuilder } from "../audio-plan.js";
import type { AudioPlan } from "../audio-plan.js";
import type { Exercise } from "../exercise.js";
import { Rng } from "../exercise.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const METRO_MIDI = 72;   // C5 — bright metronome pulse
const TARGET_MIDI = 60;  // C4 — lower, louder rhythm target
const CLICK_DUR = 0.06;  // very short, percussive

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RhythmicDictationParams {
  /** Binary attack grid, e.g. "10101010" for 4/4 eighth-note resolution. */
  pattern: string;
  meter: "4/4" | "6/8";
  /** Human-readable figure name shown in explanation, e.g. "Dotted beat 1". */
  figure: string;
}

// ─── Grader ───────────────────────────────────────────────────────────────────

export type RhythmicCellStatus = "correct" | "wrong";

export interface RhythmicDictationGrading {
  correct: boolean;
  cellStatuses: RhythmicCellStatus[];
  wrongCount: number;
  accuracy: number;
}

export type RhythmicDictationRating = "again" | "hard" | "good";

export function gradeRhythmicGrid(
  expectedPattern: string,
  userGrid: boolean[],
): RhythmicDictationGrading {
  const len = expectedPattern.length;
  const cellStatuses: RhythmicCellStatus[] = [];
  let wrongCount = 0;
  for (let i = 0; i < len; i++) {
    const expected = expectedPattern[i] === "1";
    const got = userGrid[i] ?? false;
    if (expected === got) {
      cellStatuses.push("correct");
    } else {
      cellStatuses.push("wrong");
      wrongCount++;
    }
  }
  const accuracy = len === 0 ? 1 : (len - wrongCount) / len;
  return {
    correct: wrongCount === 0,
    cellStatuses,
    wrongCount,
    accuracy,
  };
}

export function rhythmicDictationRating(grading: RhythmicDictationGrading): RhythmicDictationRating {
  if (grading.wrongCount === 0) return "good";
  if (grading.wrongCount === 1) return "hard";
  return "again";
}

// ─── Generator ───────────────────────────────────────────────────────────────

export function generateRhythmicDictation(
  itemId: string,
  params: RhythmicDictationParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const tempo = 80 + rng.int(41); // 80–120 BPM
  const audio = buildQuestionAudio(params.pattern, params.meter, tempo);
  const teach = buildTeachAudio(params.pattern, params.meter, tempo);

  return {
    itemId,
    exerciseType: "rhythmic-dictation",
    audio,
    prompt: "What rhythm did you hear?",
    options: [],
    correctAnswerId: "",
    meta: {
      seed,
      explanation: buildExplanation(params),
      distractorCount: 0,
      teachAudio: teach,
      expectedPattern: params.pattern,
      rhythmTempo: tempo,
      rhythmMeter: params.meter,
    },
  };
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function cellsPerBeat(meter: "4/4" | "6/8"): number {
  return meter === "4/4" ? 2 : 3;
}

function buildQuestionAudio(
  pattern: string,
  meter: "4/4" | "6/8",
  tempo: number,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  const cpb = cellsPerBeat(meter);
  const spb = 60 / tempo;         // seconds per beat
  const spc = spb / cpb;          // seconds per cell

  // 4-beat click-in
  for (let i = 0; i < 4; i++) {
    builder.addNote(METRO_MIDI, CLICK_DUR, i % 1 === 0 ? 0.22 : 0.14, "melody");
    builder.advance(spb);
  }

  // Pattern × 2 bars
  for (let rep = 0; rep < 2; rep++) {
    for (let cell = 0; cell < pattern.length; cell++) {
      if (cell % cpb === 0) {
        const accent = cell === 0 ? 0.22 : 0.14;
        builder.addNote(METRO_MIDI, CLICK_DUR, accent, "melody");
      }
      if (pattern[cell] === "1") {
        builder.addNote(TARGET_MIDI, spc * 0.82, 0.30, "target");
      }
      builder.advance(spc);
    }
  }

  builder.advance(spb * 0.5); // brief silence before input unlocks
  return builder.build();
}

function buildTeachAudio(
  pattern: string,
  meter: "4/4" | "6/8",
  tempo: number,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  const cpb = cellsPerBeat(meter);
  const slowTempo = Math.round(tempo * 0.70);
  const spbSlow = 60 / slowTempo;
  const spcSlow = spbSlow / cpb;
  const spbFull = 60 / tempo;
  const spcFull = spbFull / cpb;

  // 2-beat click-in at slow tempo
  for (let i = 0; i < 2; i++) {
    builder.addNote(METRO_MIDI, CLICK_DUR, i === 0 ? 0.22 : 0.14, "melody");
    builder.advance(spbSlow);
  }

  // Pattern once, slow
  for (let cell = 0; cell < pattern.length; cell++) {
    if (cell % cpb === 0) {
      builder.addNote(METRO_MIDI, CLICK_DUR, cell === 0 ? 0.22 : 0.14, "melody");
    }
    if (pattern[cell] === "1") {
      builder.addNote(TARGET_MIDI, spcSlow * 0.82, 0.30, "target");
    }
    builder.advance(spcSlow);
  }

  builder.advance(spbFull); // gap before full-tempo repeat

  // Pattern again, full tempo
  for (let cell = 0; cell < pattern.length; cell++) {
    if (cell % cpb === 0) {
      builder.addNote(METRO_MIDI, CLICK_DUR, cell === 0 ? 0.22 : 0.14, "melody");
    }
    if (pattern[cell] === "1") {
      builder.addNote(TARGET_MIDI, spcFull * 0.82, 0.30, "target");
    }
    builder.advance(spcFull);
  }

  return builder.build();
}

function durationGlyph(cells: number): string {
  if (cells === 1) return "♪";
  if (cells === 2) return "♩";
  if (cells === 3) return "♩.";
  if (cells === 4) return "𝅗";
  if (cells === 6) return "𝅗.";
  if (cells >= 8) return "○";
  return `(${cells}×♪)`;
}

function patternToNotation(pattern: string): string {
  const attacks: number[] = [];
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === "1") attacks.push(i);
  }
  if (attacks.length === 0) return "rest";
  return attacks
    .map((start, i) => {
      const end = attacks[i + 1] ?? pattern.length;
      return durationGlyph(end - start);
    })
    .join(" ");
}

function buildExplanation(params: RhythmicDictationParams): string {
  return `${params.figure} — ${patternToNotation(params.pattern)}`;
}
